import { TESTCASE_STATUS_OPTIONS } from "@regal-voice/shared-types";
import { Typography } from "antd";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions } from "../../../../atoms/RVIcon/RVIcon";
import { RVStatusBadge, type BadgeStatus } from "../../../../atoms/RVStatusBadge/RVStatusBadge";

import sharedStyles from "../RVGridCellSharedStyles.module.scss";

export const PROGRESS_STATUS_OPTIONS = TESTCASE_STATUS_OPTIONS;

export const STATUS_DISPLAY_OPTIONS = {
    [PROGRESS_STATUS_OPTIONS.IN_PROGRESS]: "In Progress",
    [PROGRESS_STATUS_OPTIONS.QUEUED]: "Queued",
    [PROGRESS_STATUS_OPTIONS.COMPLETED]: "Completed",
    [PROGRESS_STATUS_OPTIONS.FAILED]: "Failed",
    [PROGRESS_STATUS_OPTIONS.ERROR]: "Error",
    [PROGRESS_STATUS_OPTIONS.PASSED]: "Passed",
    [PROGRESS_STATUS_OPTIONS.ABORTED]: "Aborted",
} as const;

export type Status = (typeof PROGRESS_STATUS_OPTIONS)[keyof typeof PROGRESS_STATUS_OPTIONS];

type CellProps = {
    /** Current progress state for the asynchronous job. */
    status: Status;
    /** Final outcome badge to display when progress completes. */
    outcome: BadgeStatus;
    /** Text displayed next to the spinner or inside the badge. */
    content: string;
    /** Forces the progress view even for terminal states. */
    isProgressOnly?: boolean;
};

/**
 * Displays either a progress indicator or final outcome badge for asynchronous jobs rendered inside RVGrid.
 */
export const RVGridCellProgressStatus = ({ status, outcome, content, isProgressOnly }: CellProps) => {
    const inProgress = status === PROGRESS_STATUS_OPTIONS.IN_PROGRESS || status === PROGRESS_STATUS_OPTIONS.QUEUED;
    const awaitingEval =
        status === PROGRESS_STATUS_OPTIONS.IN_PROGRESS ||
        status === PROGRESS_STATUS_OPTIONS.QUEUED ||
        status === PROGRESS_STATUS_OPTIONS.COMPLETED ||
        status === PROGRESS_STATUS_OPTIONS.ERROR;

    const showProgress = awaitingEval || isProgressOnly;
    const showOutcome = !showProgress;
    const isError = status === PROGRESS_STATUS_OPTIONS.ERROR;
    return (
        <div>
            {showProgress && (
                <div>
                    {inProgress && (
                        <RVIcon className={sharedStyles.progressIcon} size={RVIconSizeOptions.XSMALL} icon="refresh" />
                    )}
                    <Typography.Text className={clsx(sharedStyles.statusText, { [sharedStyles.errorText]: isError })}>
                        {content}
                    </Typography.Text>
                </div>
            )}
            {showOutcome && <RVStatusBadge className={sharedStyles.statusBadge} content={content} status={outcome} />}
        </div>
    );
};
